import {
  TextInput,
  EmailInput,
  CountryInput,
  DateInput,
} from 'components/UserInputs'
import { RadioInputGroup } from 'components/UserInputs/RadioInputGroup'
import { PHONE_TYPE_OPTIONS, ANDROID } from '../../constants'
import { useFormikContext } from 'formik'
import { Box } from 'theme-ui'
import {
  AnyUserData,
  ConfigUtils,
  FormikOnlyValues,
  PageContext,
  StatusType,
  ViewMode,
} from 'types'
import { ErrorMessage } from 'components/Form/ErrorMessage'
import moment from 'moment'

export type BasicTeacherTrainingFields =
  | 'countryCode'
  | 'forename'
  | 'surname'
  | 'email'
  | 'device'
  | 'googlePlayEmail'
  | 'instructionDate'

export type BasicTeacherTrainingFormValues = Pick<
  AnyUserData,
  BasicTeacherTrainingFields
> & {
  status?: StatusType
}

const maxMoment = moment().add(1, 'years')
const maxDate = {
  year: maxMoment.year(),
  month: maxMoment.month() + 1,
  day: maxMoment.date(),
}

export const TeacherTrainingSection = ({
  isSupport,
  viewMode,
  configUtils,
  isDisabledField,
  shouldShowError = () => true,
  startTabIndex,
  pageContext,
}: {
  isSupport: boolean
  viewMode: ViewMode
  configUtils: ConfigUtils
  isDisabledField: (fieldName: keyof BasicTeacherTrainingFormValues) => boolean
  shouldShowError?: (fieldName: keyof BasicTeacherTrainingFormValues) => boolean
  startTabIndex: number
  pageContext: PageContext
}) => {
  const { values, errors, touched, setFieldValue, handleBlur, handleChange } =
    useFormikContext<BasicTeacherTrainingFormValues & FormikOnlyValues>()
  return (
    <>
      {viewMode === 'edit' && (
        <>
          <Box>
            <TextInput
              name="forename"
              label="First name"
              value={values.forename}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={isDisabledField('forename')}
              error={shouldShowError('forename') ? errors.forename : undefined}
              tabIndex={startTabIndex}
            />
          </Box>
          <Box>
            <TextInput
              name="surname"
              label="Last name"
              value={values.surname}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={isDisabledField('surname')}
              error={shouldShowError('surname') ? errors.surname : undefined}
              tabIndex={startTabIndex + 1}
            />
          </Box>
        </>
      )}
      <Box>
        <EmailInput
          name="email"
          label="Email"
          value={values.email}
          setFieldValue={setFieldValue}
          disabled={isDisabledField('email')}
          error={shouldShowError('email') ? errors.email : undefined}
          onBlur={handleBlur}
          tabIndex={startTabIndex + 2}
        />
      </Box>
      <Box>
        <DateInput
          label="TM instruction date"
          name="instructionDate"
          value={values.instructionDate}
          disabled={isDisabledField('instructionDate')}
          setFieldValue={(name, value: string) => {
            setFieldValue(name, value.split('T').shift())
          }}
          error={
            shouldShowError('instructionDate')
              ? errors.instructionDate
              : undefined
          }
          onBlur={handleBlur}
          tabIndex={startTabIndex + 4}
          maxDate={maxDate}
        />
        {errors.instructionDate && (
          <ErrorMessage>
            {shouldShowError('instructionDate') ? (
              <span>{errors.instructionDate}</span>
            ) : null}
          </ErrorMessage>
        )}
      </Box>
      <Box>
        <CountryInput
          label="Teaching country"
          name="countryCode"
          value={values.countryCode}
          setFieldValue={setFieldValue}
          disabled={false}
          error={
            shouldShowError('countryCode') ? errors.countryCode : undefined
          }
          onBlur={handleBlur}
          tabIndex={startTabIndex + 4}
        />
      </Box>
      <>
        {configUtils.isCountryBeta(values.countryCode) ? (
          <Box>
            <Box>
              <RadioInputGroup
                name="device"
                label="Phone type"
                value={values.device}
                options={PHONE_TYPE_OPTIONS}
                error={shouldShowError('device') ? errors.device : undefined}
                setFieldValue={setFieldValue}
                onBlur={handleBlur}
                disabled={isDisabledField('device')}
                tabIndex={startTabIndex + 5}
              />
            </Box>
            {values.device === ANDROID && (
              <Box>
                <EmailInput
                  name="googlePlayEmail"
                  label="Google play email"
                  value={values.googlePlayEmail}
                  onBlur={handleBlur}
                  disabled={isDisabledField('device')}
                  error={
                    shouldShowError('googlePlayEmail') &&
                    touched.googlePlayEmail
                      ? errors.googlePlayEmail
                      : undefined
                  }
                  tabIndex={startTabIndex + 6}
                  setFieldValue={setFieldValue}
                />
              </Box>
            )}
          </Box>
        ) : (
          <Box></Box>
        )}
      </>
    </>
  )
}
